/**
 * Offline Banner — v2
 *
 * Truth-in-advertising sync state:
 *  • Truly pending only (failed items excluded)
 *  • Failed shown SEPARATELY with a "Review" link to /sync-queue
 *  • No more "Syncing 17 pending changes…" for days when 17 are permanently stuck
 */

import { Link } from 'react-router-dom';
import { WifiOff, RefreshCw, CloudUpload, AlertTriangle } from 'lucide-react';
import { useOffline } from '@/context/OfflineContext';
import { useDeveloperMode } from '@/hooks/useDeveloperMode';

const OfflineBanner = () => {
  const { isOnline, pendingCount, failedCount, isSyncing, syncPendingActions } = useOffline();
  const developerMode = useDeveloperMode();

  // Nothing to show
  if (isOnline && pendingCount === 0 && failedCount === 0) {
    return null;
  }

  const handleSync = () => { if (!isSyncing) syncPendingActions({ force: true }); };

  // Online + syncing
  if (isOnline && isSyncing) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-blue-500 text-white px-4 py-2" data-testid="syncing-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm font-medium">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Syncing {pendingCount} pending change{pendingCount !== 1 ? 's' : ''}…</span>
        </div>
      </div>
    );
  }

  // Online + failed items present (highest priority — needs user attention)
  if (isOnline && failedCount > 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-red-500 text-white px-4 py-2" data-testid="failed-sync-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm font-medium gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {failedCount} sync issue{failedCount !== 1 ? 's' : ''} need{failedCount === 1 ? 's' : ''} attention
              {pendingCount > 0 && ` · ${pendingCount} pending`}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {developerMode ? (
              <Link to="/sync-queue" className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-md transition-colors" data-testid="review-failed-link">
                Review
              </Link>
            ) : (
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-md transition-colors disabled:opacity-60"
                data-testid="review-failed-retry"
              >
                <RefreshCw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
                Retry
              </button>
            )}
            <Link
              to="/emergency-recover"
              className="text-[11px] underline underline-offset-2 hover:text-white/80 px-1"
              data-testid="emergency-recover-link-failed"
            >
              Rescue
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Online + pending items (truly pending, no failures) — soft amber
  if (isOnline && pendingCount > 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white px-4 py-2" data-testid="pending-sync-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm font-medium gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <CloudUpload className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{pendingCount} change{pendingCount !== 1 ? 's' : ''} ready to sync</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={handleSync}
              className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-md transition-colors"
              data-testid="sync-now-btn"
            >
              <RefreshCw className="h-3 w-3" />
              Sync now
            </button>
            {/* Public, no-dev-mode-gate escape hatch. If "Sync now" silently
                fails (the SolidHome stuck-queue case), the user can still
                rescue their data without admin intervention. */}
            <Link
              to="/emergency-recover"
              className="text-[11px] underline underline-offset-2 hover:text-white/80 px-1"
              data-testid="emergency-recover-link"
            >
              Stuck?
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Offline
  return (
    <div className="fixed top-0 left-0 right-0 z-[100] px-4 py-2 bg-gray-800 text-white" data-testid="offline-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm font-medium">
        <WifiOff className="h-4 w-4 text-amber-400" />
        <span>
          You're offline
          {pendingCount > 0 && (
            <span className="ml-1 text-amber-400">— {pendingCount} change{pendingCount !== 1 ? 's' : ''} saved locally</span>
          )}
        </span>
      </div>
    </div>
  );
};

export default OfflineBanner;
